import {
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    Icon,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    Stack,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import AdminNavbarLinks from 'components/navbar/NavbarLinksAdmin';
import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { motion } from 'framer-motion';
import {
    FiArrowUpRight,
    FiBarChart2,
    FiBox,
    FiClock,
    FiDroplet,
    FiFileText,
    FiHash,
    FiLock,
    FiMonitor,
    FiNavigation,
    FiPackage,
    FiSearch,
    FiShield,
    FiShoppingBag,
    FiTool,
    FiTruck,
    FiUserCheck,
    FiUsers,
} from 'react-icons/fi';


const MotionBox = motion(Box);


const categoryTabs = [
    { category: 'All programs', icon: FiMonitor },
    { category: 'Operations', icon: FiTruck },
    { category: 'Procurement & Documents', icon: FiFileText },
    { category: 'Governance', icon: FiShield },
    { category: 'Insights & Planning', icon: FiBarChart2 },
];


const programVisuals = {
    'User Roles': { icon: FiUsers, background: '#DFF7F2', platform: '#6BC8BD', accent: '#1F8A9B' },
    Warehouse: { icon: FiBox, background: '#E4EEFC', platform: '#80A7DD', accent: '#496D96' },
    Dashboard: { icon: FiBarChart2, background: '#E1F1FC', platform: '#74B5E8', accent: '#3D7AAE' },
    'Terra System': { icon: FiDroplet, background: '#DCF6FA', platform: '#6BC9DC', accent: '#2B8CAC' },
    'Blacklist System': { icon: FiShield, background: '#E9EEF5', platform: '#94A3B8', accent: '#64748B' },
    'Generate NRP': { icon: FiHash, background: '#E7EEFC', platform: '#86A9DF', accent: '#4771A9' },
    'Sales-Ku': { icon: FiFileText, background: '#DDF4EE', platform: '#75C0AD', accent: '#358A78' },
    'Document Management': { icon: FiFileText, background: '#E4EFFB', platform: '#82A9D4', accent: '#4E78A5' },
    'Unit Management': { icon: FiTruck, background: '#E2F3FA', platform: '#76B8D5', accent: '#377AA0' },
    'Logbook Segel': { icon: FiShield, background: '#EEEAF7', platform: '#A99AC3', accent: '#7A6B91' },
    SAMS: { icon: FiTool, background: '#E1F4EB', platform: '#82C5AB', accent: '#4E8A76' },
    'Truck-In': { icon: FiTruck, background: '#FBF0DF', platform: '#D4AC6D', accent: '#A17D45' },
    Logbook: { icon: FiFileText, background: '#E6EDF8', platform: '#8BA0BD', accent: '#516B92' },
    'Purchase Order': { icon: FiShoppingBag, background: '#E0F1F0', platform: '#82B7B4', accent: '#528582' },
    'Antrian Ku': { icon: FiClock, background: '#EEE9F8', platform: '#A99ECD', accent: '#766A9D' },
    'Driver Management': { icon: FiUserCheck, background: '#F5EEDC', platform: '#C9B07B', accent: '#8C764E' },
    'Fleet Management': { icon: FiNavigation, background: '#E0F2F7', platform: '#77B7CB', accent: '#3C7E9F' },
    'Sales Order Management': { icon: FiPackage, background: '#E3F1E5', platform: '#84B995', accent: '#53866A' },
    'BOD Dashboard': { icon: FiMonitor, background: '#E5ECF7', platform: '#8DA2C5', accent: '#506E9A' },
};


const cardVariants = {
    hidden: { opacity: 0, y: 12 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.25 },
    },
};

function getCookieValue(name) {
    const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`));

    return cookie ? cookie.split('=')[1] : null;
}

function ServiceIllustration({ item, isRestricted }) {
    const visual = programVisuals[item.title] || {
        icon: FiBox,
        background: '#E5EEF6',
        platform: '#91A8BA',
        accent: '#46647E',
    };
    const IllustrationIcon = visual.icon;

    return (
        <Box
            aria-hidden="true"
            bg={visual.background}
            borderRadius="12px"
            h="84px"
            mb={2}
            overflow="hidden"
            position="relative"
        >
            <Box
                bg="rgba(24, 55, 99, 0.13)"
                borderRadius="full"
                bottom="7px"
                filter="blur(4px)"
                h="10px"
                left="50%"
                position="absolute"
                transform="translateX(-50%)"
                w="76px"
            />
            <Box
                bg="#F28B9A"
                borderRadius="10px"
                bottom="-2px"
                boxShadow="0 7px 9px rgba(15, 23, 42, 0.18)"
                h="31px"
                left="50%"
                position="absolute"
                transform="translateX(-50%) rotate(-7deg)"
                w="92px"
            />
            <Box
                bg="#5B8CF1"
                border="3px solid"
                borderColor="whiteAlpha.700"
                borderRadius="10px"
                bottom="7px"
                h="29px"
                left="50%"
                position="absolute"
                transform="translateX(-50%) rotate(-7deg)"
                w="86px"
            >
                <Box
                    bg={visual.platform}
                    borderRadius="6px"
                    h="16px"
                    left="7px"
                    position="absolute"
                    top="4px"
                    w="68px"
                />
            </Box>
            <Box
                bg={visual.platform}
                borderRadius="12px 12px 10px 10px"
                boxShadow={`0 7px 0 ${visual.accent}, 0 10px 12px rgba(15, 23, 42, 0.18)`}
                h="37px"
                left="50%"
                position="absolute"
                top="14px"
                transform="translateX(-50%) rotate(-5deg)"
                w="41px"
            />
            <Flex
                align="center"
                bg="white"
                border="2px solid"
                borderColor="whiteAlpha.900"
                borderRadius="12px 12px 10px 10px"
                boxShadow="0 5px 8px rgba(15, 23, 42, 0.14)"
                color={visual.accent}
                h="37px"
                justify="center"
                left="50%"
                position="absolute"
                top="9px"
                transform="translateX(-50%) rotate(-5deg)"
                w="41px"
            >
                <Icon as={IllustrationIcon} boxSize="22px" strokeWidth="2.4" />
            </Flex>
            {isRestricted ? (
                <Flex
                    align="center"
                    bg="whiteAlpha.800"
                    borderRadius="full"
                    color="gray.600"
                    gap={1}
                    px={2}
                    py={1}
                    position="absolute"
                    right={3}
                    top={3}
                >
                    <Icon as={FiLock} boxSize="12px" />
                    <Text fontSize="10px" fontWeight="700">
                        Locked
                    </Text>
                </Flex>
            ) : (
                <Flex
                    align="center"
                    bg="whiteAlpha.800"
                    borderRadius="full"
                    color={visual.accent}
                    gap={1}
                    px={2}
                    py={1}
                    position="absolute"
                    right={3}
                    top={3}
                >
                    <Box bg="green.400" borderRadius="full" h="6px" w="6px" />
                    <Text fontSize="10px" fontWeight="700">
                        Active
                    </Text>
                </Flex>
            )}
        </Box>
    );
}

function MenuCard({ item }) {
    const isRestricted = !item.hasAccess;
    const cardBg = useColorModeValue(isRestricted ? 'gray.50' : 'white', isRestricted ? 'gray.700' : 'gray.800');
    const titleColor = useColorModeValue(isRestricted ? 'gray.600' : 'gray.800', 'white');
    const descriptionColor = useColorModeValue('gray.600', 'gray.300');

    return (
        <MotionBox
            as={isRestricted ? 'div' : 'a'}
            aria-disabled={isRestricted}
            cursor={isRestricted ? 'not-allowed' : 'pointer'}
            display="block"
            filter={isRestricted ? 'grayscale(0.45)' : undefined}
            href={isRestricted ? undefined : item.href}
            variants={cardVariants}
            whileHover={isRestricted ? undefined : { y: -5 }}
            whileTap={isRestricted ? undefined : { scale: 0.99 }}
            bg={cardBg}
            borderRadius="16px"
            boxShadow={isRestricted ? '0 3px 10px rgba(15, 23, 42, 0.035)' : '0 6px 16px rgba(30, 94, 146, 0.08)'}
            minH="190px"
            opacity={isRestricted ? 0.78 : 1}
            overflow="hidden"
            p={2}
            transition="box-shadow 0.2s ease"
            _hover={
                isRestricted
                    ? undefined
                    : {
                          boxShadow: '0 18px 38px rgba(15, 23, 42, 0.14)',
                          textDecoration: 'none',
                      }
            }
        >
            <ServiceIllustration item={item} isRestricted={isRestricted} />
            <Flex align="flex-start" gap={2} justify="space-between" px={2} pb={2}>
                <Stack spacing={1}>
                    <Heading color={titleColor} fontSize="sm" lineHeight="short">
                        {item.title}
                    </Heading>
                    <Text color={descriptionColor} fontSize="xs" lineHeight="tall">
                        {item.description}
                    </Text>
                    {isRestricted && (
                        <Text color="gray.500" fontSize="xs" pt={1}>
                            Ask IT to request access.
                        </Text>
                    )}
                </Stack>
                {!isRestricted && <Icon as={FiArrowUpRight} color="blue.500" boxSize="17px" flexShrink={0} mt={1} />}
            </Flex>
        </MotionBox>
    );
}

function HubFooter() {
    return (
        <Box as="footer" maxW="1920px" mx="auto" px={{ base: 4, md: 8, xl: 10 }} pb={{ base: 5, md: 7 }}>
            <Flex
                align={{ base: 'center', sm: 'flex-start' }}
                borderTop="1px solid"
                borderColor="whiteAlpha.300"
                color="whiteAlpha.800"
                direction={{ base: 'column', sm: 'row' }}
                fontSize="sm"
                justify="space-between"
                gap={2}
                pt={5}
                textAlign={{ base: 'center', sm: 'left' }}
            >
                <Text>© {new Date().getFullYear()} Sinar Alam</Text>
                <Text color="whiteAlpha.700">Super App · One workspace for every program</Text>
            </Flex>
        </Box>
    );
}

export default function Dashboard() {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('All programs');
    const [isScrolled, setIsScrolled] = useState(false);
    useEffect(() => {
        const updateScrollState = () => setIsScrolled(window.scrollY > 8);

        updateScrollState();
        window.addEventListener('scroll', updateScrollState);
        return () => window.removeEventListener('scroll', updateScrollState);
    }, []);
    const isLocalEnvironment = ['development', 'local'].includes(process.env.REACT_APP_ENV);
    const apiHost = process.env.REACT_APP_API_HOST;
    const contentSurfaceColor = useColorModeValue('rgba(248, 252, 255, 0.93)', 'rgba(17, 24, 39, 0.92)');
    const titleColor = useColorModeValue('gray.800', 'white');
    const mutedColor = useColorModeValue('gray.600', 'gray.300');
    const categoryBg = useColorModeValue('white', 'gray.800');
    const categoryNavBg = useColorModeValue('rgba(226, 240, 247, 0.78)', 'whiteAlpha.100');
    const categoryNavBorder = useColorModeValue('rgba(129, 162, 184, 0.22)', 'whiteAlpha.200');
    const userName = decodeURIComponent(getCookieValue('user') || 'User').replace(/^"|"$/g, '');

    const getAuthUrl = (host, hostDev) => (isLocalEnvironment ? hostDev : host);

    const token = getCookieValue('token');
    const decoded = token ? jwtDecode(token) : null;

    const showTeraTangki = decoded?.roles?.includes('tera:web:tbbmkuin:view');
    const showAuth = decoded?.roles?.includes('auth:web:master:view');
    const showWarehouse = decoded?.roles?.includes('warehouse:web:master:view');
    const showDashboard = decoded?.roles?.includes('dashboard:web:master:view');
    const showGeneratedNRP = decoded?.roles?.includes('generated-nrp:web:master:view');
    const showBlacklist = decoded?.roles?.includes('blacklist:data:data-blacklist:view');
    const showPenawaran = decoded?.roles?.includes('penawaran:web:master:view');
    const showLambung = decoded?.roles?.includes('lambung:web:master:view');
    const showSegel = decoded?.roles?.includes('segel:web:master:view');
    const showWorkshop = decoded?.roles?.includes('workshop:web:master:view');
    const showTruckIn = decoded?.roles?.includes('unitlog:web:default:view');
    const showLogbook = decoded?.roles?.includes('logbook:web:master:view');
    const showPurchaseOrder = decoded?.roles?.includes('purchase-order:web:default:view');
    const showAntrianku = decoded?.roles?.includes('antrian:web:default:view');
    const showDriverManagement = decoded?.roles?.includes('driver-management:web:default:view');
    const showSalesOrderManagement = decoded?.roles?.includes('sales-order-management:web:default:view');
    const showBodDashboard = decoded?.roles?.includes('bod-dashboard:web:default:view');
    const showAntaranku = Boolean(
        decoded?.roles?.some(
            (role) =>
                typeof role === 'string' &&
                (role.startsWith('antaranku:web:kepala_pool:') ||
                    role.startsWith('antaranku:web:admin_spp:') ||
                    role.startsWith('antaranku:web:superadmin:')),
        ),
    );
    const showDocumentManagement = decoded?.roles?.includes('document-management-service:web:default:view');

    const menuItems = [
        {
            href: getAuthUrl(process.env.REACT_APP_AUTH, `${process.env.REACT_APP_API_HOST}:3000/master`),
            title: 'User Roles',
            description: 'Manage users, roles, and permissions.',
            category: 'Governance',
            hasAccess: showAuth,
            hideWhenUnauthorized: true,
        },
        {
            href: `${process.env.REACT_APP_API_HOST}:3001`,
            title: 'Warehouse',
            description: 'Track stock, receipts, and distribution.',
            category: 'Operations',
            hasAccess: showWarehouse,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_DASHBOARD, `${process.env.REACT_APP_API_HOST}:3010/dashboard`),
            title: 'Dashboard',
            description: 'Review operational performance at a glance.',
            category: 'Insights & Planning',
            hasAccess: showDashboard,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_TERRA, `${process.env.REACT_APP_API_HOST}:3003/terra`),
            title: 'Terra System',
            description: 'Manage tank calibration activities.',
            category: 'Operations',
            hasAccess: showTeraTangki,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_BL, `${process.env.REACT_APP_API_HOST}:3004/blacklist`),
            title: 'Blacklist System',
            description: 'Review and verify blacklist records.',
            category: 'Governance',
            hasAccess: showBlacklist,
        },
        {
            href: 'http://ops.sinaralam.com/akerp/content/erp/id.php',
            title: 'Generate NRP',
            description: 'Generate employee identification numbers.',
            category: 'Governance',
            hasAccess: showGeneratedNRP,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_PENAWARAN, `${process.env.REACT_APP_API_HOST}:3005/penawaran`),
            title: 'Sales-Ku',
            description: 'Create and manage commercial quotations.',
            category: 'Procurement & Documents',
            hasAccess: showPenawaran,
        },
        {
            href: process.env.REACT_APP_DMS_URL,
            title: 'Document Management',
            description: 'Store, find, and manage business documents.',
            category: 'Procurement & Documents',
            hasAccess: Boolean(showDocumentManagement && process.env.REACT_APP_DMS_URL),
        },
        {
            href: getAuthUrl(process.env.REACT_APP_LAMBUNG, `${process.env.REACT_APP_API_HOST}:3006/lambung`),
            title: 'Unit Management',
            description: 'Access Lambung operational tools.',
            category: 'Operations',
            hasAccess: showLambung,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_SEGEL, `${process.env.REACT_APP_API_HOST}:3007/segel`),
            title: 'Logbook Segel',
            description: 'Record seal and verification activities.',
            category: 'Operations',
            hasAccess: showSegel,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_WORKSHOP, `${process.env.REACT_APP_API_HOST}:3012/workshop`),
            title: 'SAMS',
            description: 'Coordinate workshop maintenance work.',
            category: 'Operations',
            hasAccess: showWorkshop,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_UNITLOG, `${apiHost}:3022/truck-in/`),
            title: 'Truck-In',
            description: 'Manage inbound truck activity.',
            category: 'Operations',
            hasAccess: showTruckIn,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_LOGBOOK, `${process.env.REACT_APP_LOGBOOK}:3012/workshop`),
            title: 'Logbook',
            description: 'Capture daily operational logs.',
            category: 'Operations',
            hasAccess: showLogbook,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_PURCHASE_ORDER, `${process.env.REACT_APP_API_HOST}:3013/purchase-order`),
            title: 'Purchase Order',
            description: 'Create and follow up purchase orders.',
            category: 'Procurement & Documents',
            hasAccess: showPurchaseOrder,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_ANTRIAN_KU, `${process.env.REACT_APP_API_HOST}:3018/antrian`),
            title: 'Antrian Ku',
            description: 'Monitor and manage service queues.',
            category: 'Operations',
            hasAccess: showAntrianku,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_DRIVER_MANAGEMENT, `${process.env.REACT_APP_API_HOST}:3017/driver-management`),
            title: 'Driver Management',
            description: 'Manage driver data and assignments.',
            category: 'Operations',
            hasAccess: showDriverManagement,
        },
        {
            href: getAuthUrl(process.env.REACT_APP_ANTARANKU, `${process.env.REACT_APP_API_HOST}:3103/antaranku`),
            title: 'Fleet Management',
            description: 'Manage fleet availability and movement.',
            category: 'Operations',
            hasAccess: showAntaranku,
        },
        {
            href: process.env.REACT_APP_SALES_ORDER_MANAGEMENT,
            title: 'Sales Order Management',
            description: 'Manage sales orders and fulfilment.',
            category: 'Procurement & Documents',
            hasAccess: showSalesOrderManagement,
        },
        {
            href: process.env.REACT_APP_BOD_DASHBOARD,
            title: 'BOD Dashboard',
            description: 'View executive-level business insight.',
            category: 'Insights & Planning',
            hasAccess: showBodDashboard,
        },
    ];

    const visibleMenuItems = menuItems.filter((item) => item.hasAccess || !item.hideWhenUnauthorized);
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchingItems = visibleMenuItems.filter(
        (item) =>
            (activeCategory === 'All programs' || item.category === activeCategory) &&
            `${item.title} ${item.description}`.toLowerCase().includes(normalizedSearch),
    );
    const activeItems = matchingItems.filter((item) => item.hasAccess);
    const restrictedItems = matchingItems.filter((item) => !item.hasAccess);
    const accessibleItemCount = visibleMenuItems.filter((item) => item.hasAccess).length;
    const restrictedItemCount = visibleMenuItems.length - accessibleItemCount;
    const accessibleItemLabel = accessibleItemCount === 1 ? 'program' : 'programs';

    return (
        <Box
            minH="100vh"
            bg="#057ca8"
            bgImage={`linear-gradient(rgba(0, 96, 139, 0.82), rgba(0, 126, 158, 0.82)), url(${process.env.PUBLIC_URL}/BG_Menu.png)`}
            bgPosition="center"
            bgSize="cover"
            bgAttachment={{ base: 'scroll', md: 'fixed' }}
        >
            <Box
                as="header"
                position="sticky"
                top="0"
                zIndex="10"
                bg={isScrolled ? 'rgba(0, 68, 98, 0.96)' : 'transparent'}
                backdropFilter={isScrolled ? 'blur(14px)' : 'none'}
                boxShadow={isScrolled ? '0 6px 20px rgba(0, 42, 64, 0.2)' : 'none'}
                transition="background-color 0.2s ease, box-shadow 0.2s ease"
            >
                <Flex
                    align="center"
                    justify="space-between"
                    maxW="1920px"
                    minH="64px"
                    mx="auto"
                    px={{ base: 4, md: 8, xl: 10 }}
                    gap={4}
                >
                    <Image
                        src={`${process.env.PUBLIC_URL}/saraya-wordmark.png`}
                        alt="Saraya"
                        display={{ base: 'none', sm: 'block' }}
                        h={{ base: '36px', md: '44px' }}
                        w="auto"
                    />
                    <AdminNavbarLinks showSidebarResponsive={false} variant="hub" />
                </Flex>
            </Box>

            <Box color="white">
                <Box
                    maxW="1920px"
                    mx="auto"
                    pb={{ base: 5, md: 5 }}
                    pt={{ base: 8, md: 8 }}
                    px={{ base: 5, md: 8, xl: 10 }}
                >
                    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 8, lg: 16 }} alignItems="center">
                        <Box>
                            <Badge
                                bg="whiteAlpha.200"
                                borderRadius="full"
                                color="white"
                                fontSize="xs"
                                letterSpacing="wide"
                                mb={3}
                                px={3}
                                py={1}
                            >
                                YOUR WORKSPACE
                            </Badge>
                            <Heading fontSize={{ base: '3xl', md: '4xl' }} lineHeight="shorter" maxW="650px">
                                Good to see you, {userName}.
                            </Heading>
                            <Text color="whiteAlpha.850" fontSize={{ base: 'md', md: 'lg' }} maxW="590px" mt={3}>
                                Find the programs you need, continue operational work, and move between systems from one place.
                            </Text>
                            <Flex
                                align="flex-start"
                                borderLeft="2px solid"
                                borderColor="whiteAlpha.600"
                                gap={3}
                                maxW="650px"
                                mt={4}
                                pl={4}
                            >
                                <Box bg="whiteAlpha.250" borderRadius="10px" flexShrink={0} p={2}>
                                    <Icon as={FiMonitor} boxSize="18px" />
                                </Box>
                                <Box>
                                    <Text color="whiteAlpha.800" fontSize="xs" fontWeight="700" letterSpacing="wide">
                                        SARAYA SUPER APP
                                    </Text>
                                    <Text color="whiteAlpha.900" fontSize="sm" lineHeight="tall" mt={1}>
                                        Satu pintu kerja digital yang menyatukan layanan operasional, dokumen, tata kelola, dan insight agar setiap tim bergerak lebih terhubung, cepat, dan terarah.
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <InputGroup maxW={{ lg: '500px' }} justifySelf={{ lg: 'end' }}>
                            <InputLeftElement h="58px" pointerEvents="none">
                                <Icon as={FiSearch} color="gray.500" boxSize="20px" />
                            </InputLeftElement>
                            <Input
                                aria-label="Search programs"
                                bg="white"
                                border="none"
                                borderRadius="14px"
                                color="gray.800"
                                fontSize="md"
                                h="58px"
                                placeholder="Search programs, tools, or services"
                                pl="52px"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                _placeholder={{ color: 'gray.500' }}
                                _focus={{ boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.35)' }}
                            />
                        </InputGroup>
                    </SimpleGrid>
                </Box>
            </Box>

            <Box
                maxW="1920px"
                mx="auto"
                pb={{ base: 6, md: 8 }}
                pt={{ base: 5, md: 5 }}
                px={{ base: 4, md: 8, xl: 10 }}
            >
                <Box
                    bg={contentSurfaceColor}
                    backdropFilter="blur(18px)"
                    border="1px solid"
                    borderColor="whiteAlpha.500"
                    borderRadius={{ base: '20px', md: '28px' }}
                    boxShadow="0 20px 48px rgba(0, 52, 78, 0.18)"
                    p={{ base: 5, md: 8 }}
                >
                <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4} mb={6} wrap="wrap">
                    <Box>
                        <Flex align="center" gap={3}>
                            <Image
                                src={`${process.env.PUBLIC_URL}/logosinaralam.png`}
                                alt="Sinar Alam Group"
                                h={{ base: '24px', md: '30px' }}
                                maxW={{ base: '130px', md: '160px' }}
                                objectFit="contain"
                            />
                            <Box h="28px" borderLeft="1px solid" borderColor="gray.300" />
                            <Text color={titleColor} fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                                Explore programs
                            </Text>
                        </Flex>
                        <Text color={mutedColor} fontSize="sm" mt={1}>
                            {accessibleItemCount} {accessibleItemLabel} ready for your role
                            {restrictedItemCount ? ` · ${restrictedItemCount} more available through IT` : '.'}
                        </Text>
                    </Box>

                    <Box
                        bg={categoryNavBg}
                        border="1px solid"
                        borderColor={categoryNavBorder}
                        borderRadius="18px"
                        maxW="full"
                        overflowX="auto"
                        p={1}
                    >
                        <Flex gap={1} minW={{ base: '730px', md: 'auto' }} role="tablist">
                            {categoryTabs.map(({ category, icon }) => {
                                const isActive = activeCategory === category;

                                return (
                                    <Button
                                        key={category}
                                        aria-selected={isActive}
                                        bg={isActive ? '#1459C7' : 'transparent'}
                                        borderRadius="13px"
                                        boxShadow={isActive ? '0 6px 14px rgba(20, 89, 199, 0.24)' : 'none'}
                                        color={isActive ? 'white' : titleColor}
                                        h="42px"
                                        minW="max-content"
                                        onClick={() => setActiveCategory(category)}
                                        px={3}
                                        role="tab"
                                        transition="background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease"
                                        variant="unstyled"
                                        _hover={{
                                            bg: isActive ? '#104BAA' : 'whiteAlpha.600',
                                            transform: 'translateY(-1px)',
                                        }}
                                    >
                                        <Flex align="center" gap={2}>
                                            <Flex
                                                align="center"
                                                bg={isActive ? 'whiteAlpha.220' : 'whiteAlpha.800'}
                                                borderRadius="9px"
                                                color={isActive ? 'white' : 'blue.600'}
                                                h="26px"
                                                justify="center"
                                                w="26px"
                                            >
                                                <Icon as={icon} boxSize="14px" />
                                            </Flex>
                                            <Text fontSize="sm" fontWeight="700" whiteSpace="nowrap">
                                                {category}
                                            </Text>
                                        </Flex>
                                    </Button>
                                );
                            })}
                        </Flex>
                    </Box>
                </Flex>

                <Stack spacing={{ base: 8, md: 10 }}>
                    {activeItems.length > 0 && (
                        <Box>
                            <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={2} mb={4}>
                                <Box>
                                    <Heading color={titleColor} fontSize={{ base: 'xl', md: '2xl' }}>
                                        Active applications
                                    </Heading>
                                    <Text color={mutedColor} fontSize="sm" mt={1}>
                                        Programs ready to open with your current role.
                                    </Text>
                                </Box>
                                <Badge borderRadius="full" colorScheme="green" px={3} py={1}>
                                    {activeItems.length} active
                                </Badge>
                            </Flex>
                            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 7 }} spacing={{ base: 4, md: 5 }}>
                                {activeItems.map((item) => (
                                    <MenuCard key={item.title} item={item} />
                                ))}
                            </SimpleGrid>
                        </Box>
                    )}

                    {restrictedItems.length > 0 && (
                        <Box>
                            <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={2} mb={4}>
                                <Box>
                                    <Heading color={titleColor} fontSize={{ base: 'xl', md: '2xl' }}>
                                        More applications from IT
                                    </Heading>
                                    <Text color={mutedColor} fontSize="sm" mt={1}>
                                        Request access to use programs assigned to another role.
                                    </Text>
                                </Box>
                                <Badge borderRadius="full" colorScheme="gray" px={3} py={1}>
                                    {restrictedItems.length} to explore
                                </Badge>
                            </Flex>
                            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 7 }} spacing={{ base: 4, md: 5 }}>
                                {restrictedItems.map((item) => (
                                    <MenuCard key={item.title} item={item} />
                                ))}
                            </SimpleGrid>
                        </Box>
                    )}
                </Stack>

                {!matchingItems.length && (
                    <Box bg={categoryBg} borderRadius="20px" py={16} px={6} textAlign="center">
                        <Heading color={titleColor} fontSize="xl">
                            No programs found
                        </Heading>
                        <Text color={mutedColor} mt={2}>
                            Try another search term or choose a different category.
                        </Text>
                    </Box>
                )}
            </Box>
                </Box>

            <HubFooter />
        </Box>
    );
}
